/**
 * DatePicker — Phase 6a.9 introduction / 6a.9c text-first rework.
 *
 * Text-first input where the auditor types `DD.MM.YYYY` directly. A small
 * calendar icon button at the trailing edge opens the shadcn `<Popover>`
 * + `<Calendar>` composition for users who prefer point-and-click.
 *
 * Why text-first: auditors enter dozens of dates per engagement and
 * `31.12.2025` is significantly faster than navigating through months in
 * a calendar grid. The popover is kept as an opt-in fallback.
 *
 * External API (preserved from Phase 6a.9 — no consumer rewrites):
 *   • `value?: Date`        — receives a JS `Date` (the Zod schema is
 *                              `z.date()`, react-hook-form passes Date).
 *   • `onChange?: (Date | undefined) => void`
 *                            — emits `Date` on valid typed entry or
 *                              calendar selection, `undefined` when the
 *                              input is cleared. Invalid keystrokes do
 *                              NOT emit (form stays clean).
 *
 * Display format is `DD.MM.YYYY` in both TR and EN UI modes per design
 * directive (Turkish standard, predictable for auditors). The calendar
 * widget honours i18n's locale for headers/month names.
 */

import { enUS, tr } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import {
  dateToDisplay,
  dateToIso,
  isoToDate,
  parseDisplayToIso,
} from "./date-picker.utils";

interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Optional id for label association. */
  id?: string;
  /** Optional aria-invalid flag for form-error styling. */
  "aria-invalid"?: boolean;
}

export function DatePicker({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  id,
  ...rest
}: DatePickerProps) {
  const { i18n, t } = useTranslation();
  const calendarLocale = i18n.language?.startsWith("tr") ? tr : enUS;
  const isAriaInvalid = rest["aria-invalid"];

  // Local text state mirrors the input value as the user types. On valid
  // parse → onChange(Date); on invalid → local error, no onChange call.
  const [text, setText] = React.useState<string>(dateToDisplay(value));
  const [localError, setLocalError] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);

  // External `value` prop changes (e.g., form reset, calendar selection
  // from another path) must re-sync the local display string.
  React.useEffect(() => {
    setText(dateToDisplay(value));
    setLocalError(null);
  }, [value]);

  function handleTextChange(raw: string) {
    setText(raw);
    if (raw === "") {
      setLocalError(null);
      onChange?.(undefined);
      return;
    }
    const iso = parseDisplayToIso(raw);
    if (iso === null) {
      // Show inline error but don't pollute form state with a Date that
      // round-trips back wrong — onChange stays uninvoked.
      setLocalError(t("validation.invalidDate"));
      return;
    }
    setLocalError(null);
    onChange?.(isoToDate(iso));
  }

  function handleCalendarSelect(date: Date | undefined) {
    if (!date) {
      setText("");
      setLocalError(null);
      onChange?.(undefined);
      setOpen(false);
      return;
    }
    // react-day-picker emits the selected day at LOCAL-timezone midnight.
    // The rest of this app (Zod schema, ISO serialization on submit, the
    // calendar's own `selected` prop) operates on UTC-midnight Dates. In
    // a positive-UTC-offset zone like Europe/Istanbul, local midnight on
    // June 15 is June 14 in UTC — so we must reinterpret the click's
    // local calendar day as the same day at UTC midnight before emitting.
    const normalized = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    const iso = dateToIso(normalized);
    setText(dateToDisplay(isoToDate(iso)));
    setLocalError(null);
    onChange?.(isoToDate(iso));
    setOpen(false);
  }

  const effectiveInvalid = isAriaInvalid || localError != null;

  return (
    <div className={cn("space-y-1", className)}>
      <div className="relative">
        <Input
          id={id}
          type="text"
          inputMode="numeric"
          value={text}
          onChange={(e) => handleTextChange(e.target.value)}
          placeholder={placeholder ?? "GG.AA.YYYY"}
          disabled={disabled}
          aria-invalid={effectiveInvalid || undefined}
          autoComplete="off"
          className="pr-10"
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled}
              aria-label={t("engagement.creation.openCalendar")}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={value}
              defaultMonth={value}
              onSelect={handleCalendarSelect}
              locale={calendarLocale}
              autoFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      {localError && (
        <p
          role="alert"
          data-testid="date-picker-error"
          className="text-xs text-destructive"
        >
          {localError}
        </p>
      )}
    </div>
  );
}

export { parseDisplayToIso, formatIsoToDisplay } from "./date-picker.utils";
